/**
 * FinanceSettingsLayout
 *
 * Wraps Finance Settings sub-pages (Accounting Setup, Approval Policies, etc.) with the shared T5
 * settings nav (Organization / Modules / Administration, PAGE_TEMPLATES.md § T5), so users can
 * move between settings sections without going back to the main sidebar. The classic Finance
 * sub-nav was retired with the classic variant (#7715).
 */

import { useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { SettingsNavFrame } from "@/components/templates";
import { settingsNavActiveId } from "@/components/settings/settingsNav";
import { SettingsNavHostContext } from "@/components/settings/SettingsNavHost";
import { useSettingsNav } from "@/components/settings/useSettingsNav";

/**
 * Finance settings pages that draw the T5 nav themselves, by route segment: they render
 * `SettingsPageTemplate` and pass it `useSettingsNavHost()`. Every other Finance settings page
 * gets the same nav drawn beside it here, so none is left without one. A page's T5 migration adds
 * its segment to this list in the same change that passes the nav to the template.
 */
const PAGES_DRAWING_THE_NAV: ReadonlySet<string> = new Set(["allocations", "ap-inbound-email", "approval-policies", "bank-connections", "contribution-defaults", "integrations", "payables-settings", "recurring-rules"]);

interface FinanceSettingsLayoutProps {
  children: ReactNode;
}

export function FinanceSettingsLayout({ children }: FinanceSettingsLayoutProps) {
  const { pathname } = useLocation();
  const nav = useSettingsNav();
  const activeId = settingsNavActiveId(nav, pathname);
  const host = useMemo(() => ({ nav, activeId }), [nav, activeId]);
  const segment = pathname.toLowerCase().replace(/\/+$/, "").split("/").pop() ?? "";

  // A page that draws the nav gets it through the host context. Every other page gets the frame
  // SettingsPageTemplate draws, in the same centred page width (PageLayout's), so the nav sits in
  // the same place on every Finance settings page.
  const content = PAGES_DRAWING_THE_NAV.has(segment) ? (
    <SettingsNavHostContext.Provider value={host}>{children}</SettingsNavHostContext.Provider>
  ) : (
    <div className="mx-auto w-full max-w-[var(--layout-max-width)]">
      <SettingsNavFrame nav={nav} activeId={activeId}>
        {children}
      </SettingsNavFrame>
    </div>
  );

  return <AppLayout>{content}</AppLayout>;
}
